import React, { useMemo, useState } from "react";

import { Autocomplete, Box, Button, Paper, SxProps, TextField, Theme, Typography } from "@mui/material";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { useInvestorsSelector } from "store";

type InvestorOption = { id: number; name: string };
type SeparatorOption = { id: string; name: string; isSeparator: true };
type InvestorOptionType = InvestorOption | SeparatorOption;

interface Props {
  sx?: SxProps<Theme>;
  disabled?: boolean;
  loading?: boolean;
  clientInvestors: { id: number; name: string }[];
  preselectedInvestorId: number | null;
  onChange: (value: { id: number; name: string } | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const ClientEntityAutocomplete: React.FC<Props> = ({
  disabled,
  loading,
  clientInvestors,
  preselectedInvestorId,
  onChange,
  label,
  sx,
  error,
  helperText
}) => {
  const investors = useInvestorsSelector();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const investorsFromStore = useMemo(
    () => investors.map((investor) => ({ id: investor.id, name: investor.name })),
    [investors]
  );

  const investorsOptions = useMemo<InvestorOptionType[]>(() => {
    const accountInvestors = investorsFromStore.filter(
      (investor) => !clientInvestors.some((dealInvestor) => dealInvestor.id === investor.id)
    );

    const options: InvestorOptionType[] = [];

    if (clientInvestors.length) {
      options.push({ id: "separator-client", name: "CLIENT INVESTORS", isSeparator: true });
      options.push(...clientInvestors);
    }
    if (accountInvestors.length) {
      options.push({ id: "separator-account", name: "ACCOUNT INVESTORS", isSeparator: true });
      options.push(...accountInvestors);
    }

    return options;
  }, [investorsFromStore, clientInvestors]);

  const handleInvestorSelect = ({ investor_id }: { investor_id: number }) => {
    onChange({ id: investor_id, name: investors.find((investor) => investor.id === investor_id)?.name || "" });
    setIsDrawerOpen(false);
  };

  return (
    <>
      <Autocomplete
        disabled={disabled}
        loading={loading}
        sx={{ width: "100%", ...sx }}
        options={investorsOptions}
        value={investorsOptions.find((opt) => !("isSeparator" in opt) && opt.id === preselectedInvestorId) || null}
        getOptionLabel={(option) => {
          if ("isSeparator" in option) return "";
          return option.name;
        }}
        onChange={(_, value) => {
          if (value && !("isSeparator" in value)) {
            onChange({ id: value.id, name: value.name });
          } else {
            onChange(null);
          }
        }}
        slots={{
          paper: (props) => (
            <Paper
              {...props}
              sx={[
                ...(props.sx ? (Array.isArray(props.sx) ? props.sx : [props.sx]) : []),
                {
                  position: "relative",
                  backgroundColor: "white",
                  zIndex: 1500,
                  borderRadius: "10px",
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: "28vh",
                    "& .MuiAutocomplete-option[aria-disabled='true']": {
                      opacity: 1,
                      "&.Mui-focused": {
                        opacity: 1
                      }
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      bgcolor: "#edf1f9",
                      "&.Mui-focused": {
                        bgcolor: "#edf1f9"
                      }
                    }
                  },
                  "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                    bgcolor: "#edf1f9"
                  }
                }
              ]}
            >
              {props.children}
              <Button
                onMouseDown={() => {
                  setIsDrawerOpen(true);
                }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  height: "50px",
                  backgroundColor: "#fff",
                  boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                  borderRadius: "0 0 10px 10px"
                }}
                fullWidth
              >
                Add Investor
              </Button>
            </Paper>
          )
        }}
        slotProps={{
          popper: {
            placement: "bottom-start",
            modifiers: [
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: ["top-start"],
                  boundary: "viewport",
                  padding: 8
                }
              },
              { name: "preventOverflow", enabled: true, options: { rootBoundary: "viewport", padding: 8 } }
            ]
          }
        }}
        renderOption={(props, option) => {
          if ("isSeparator" in option) {
            return (
              <Box
                key={option.name}
                display="flex"
                alignItems="center"
                padding="6px 20px"
                sx={{ pointerEvents: "none", backgroundColor: "#F6F8FC" }}
              >
                <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
                  {option.name}
                </Typography>
              </Box>
            );
          }
          return (
            <li
              style={{
                minHeight: "43px",
                fontSize: "16px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                display: "flex",
                alignItems: "center"
              }}
              {...props}
              key={option.id}
            >
              {option.name}
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => {
          if ("isSeparator" in option || (value && "isSeparator" in value)) return false;
          if (!value || "isSeparator" in value) return false;
          return option.id === value.id;
        }}
        getOptionDisabled={(option) => "isSeparator" in option}
        renderInput={(params) => {
          return (
            <TextField
              placeholder="Select"
              {...params}
              label={label}
              error={error}
              helperText={helperText}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          );
        }}
      />
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          handleInvestorSelect({ investor_id: response.id });
        }}
      />
    </>
  );
};
